/**
 * FullscreenButton Component
 *
 * Botão para alternar modo fullscreen do artefato
 */

import React, { memo } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface FullscreenButtonProps {
  isFullscreen?: boolean;
  onToggle: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// =============================================================================
// ICONS
// =============================================================================

const ExpandIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
      fill="currentColor"
    />
  </svg>
);

const CollapseIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
      fill="currentColor"
    />
  </svg>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const FullscreenButton: React.FC<FullscreenButtonProps> = memo(
  ({ isFullscreen = false, onToggle, className = '', size = 'md' }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle();
    };

    // Tamanhos
    const iconSizes = { sm: 14, md: 16, lg: 20 };
    const iconSize = iconSizes[size];

    // Classes de tamanho
    const sizeClasses = {
      sm: 'p-1',
      md: 'p-1.5',
      lg: 'p-2',
    };

    const label = isFullscreen ? 'Sair do fullscreen' : 'Fullscreen';

    return (
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center justify-center
          rounded-md transition-all duration-200
          text-gray-400 hover:text-white hover:bg-white/10
          focus:outline-none focus:ring-2 focus:ring-yellow-500/50
          ${sizeClasses[size]}
          ${className}
        `}
        aria-label={label}
        title={label}
      >
        {isFullscreen ? <CollapseIcon size={iconSize} /> : <ExpandIcon size={iconSize} />}
      </button>
    );
  }
);

FullscreenButton.displayName = 'FullscreenButton';

export default FullscreenButton;
